import { useMemo } from 'react';

import type {
  FormField,
  FormFieldChoice,
  FormFieldLocalization,
} from '../../types';
import { SectionCard } from './SectionCard';

/**
 * Variables / Fields, rendered as a clean reference table.
 *
 * Layout: two grouped tables, both with the same column shape.
 *   1. **Universal fields** — fields that are not country-gated (or whose
 *      gating only touches choice values, not visibility). Renders first.
 *   2. **Country-specific fields** — fields hidden by default and only
 *      shown for some geographies, per the parsed catalog client scripts /
 *      UI policies. Each row carries a chip listing the countries the
 *      field is enabled in.
 *
 * Within each section:
 *   - Mandatory: red `*` after the label.
 *   - Read-only: a soft eye pill (the dedicated column flag).
 *   - Reference: friendly target on a second line under the type pill.
 *   - Choice fields: full-width row with chip-cards. Localization-only
 *     values (choices ServiceNow's catalog scripts add inside a country
 *     branch) get an amber background + tooltip. A legend below the
 *     section explains the marker.
 *
 * Row ordering: Requested By / Requested For pinned to the top of
 * Universal — that's how an LPO walks through the form.
 */
export function FieldsTable({
  fields,
  hasConditionalLogic = false,
  testTicketUrl,
  onOpenKb,
}: {
  fields: FormField[];
  hasConditionalLogic?: boolean;
  testTicketUrl?: string | null;
  /** Opens the KB drawer; when omitted the notice falls back to plain text. */
  onOpenKb?: () => void;
}) {
  const ordered = useMemo(() => sortRequesterFirst(fields), [fields]);
  const universal = useMemo(
    () => ordered.filter((f) => placementOf(f) !== 'country_specific'),
    [ordered],
  );
  const countrySpecific = useMemo(
    () => ordered.filter((f) => placementOf(f) === 'country_specific'),
    [ordered],
  );

  const hasLocalizationChoices = useMemo(
    () => fields.some((f) => (f.choices ?? []).some(isLocalizationChoice)),
    [fields],
  );
  const hasRuntimeRebuilt = useMemo(
    () => fields.some((f) => (f.choices ?? []).some((c) => c.rebuilt_at_runtime)),
    [fields],
  );

  return (
    <SectionCard
      title="Variables / Fields"
      count={fields.length}
      defaultOpen
      emptyHint="No variables on this form."
    >
      {hasConditionalLogic && (
        <ConditionalLogicNotice
          testTicketUrl={testTicketUrl}
          onOpenKb={onOpenKb}
        />
      )}
      {universal.length > 0 && (
        <FieldGrid title="Universal fields" fields={universal} />
      )}
      {countrySpecific.length > 0 && (
        <FieldGrid
          title="Country-specific fields"
          fields={countrySpecific}
          showCountryColumn
        />
      )}
      {(hasLocalizationChoices || hasRuntimeRebuilt) && (
        <ChoiceLegend
          showAmber={hasLocalizationChoices}
          showRuntime={hasRuntimeRebuilt}
        />
      )}
    </SectionCard>
  );
}

function FieldGrid({
  title,
  fields,
  showCountryColumn = false,
}: {
  title: string;
  fields: FormField[];
  showCountryColumn?: boolean;
}) {
  if (fields.length === 0) return null;
  return (
    <div className="mb-5 last:mb-0">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
        <span className="ml-2 font-normal text-slate-400">{fields.length}</span>
      </h3>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
              <Th className={showCountryColumn ? 'w-[28%]' : 'w-[36%]'}>Label</Th>
              <Th className="w-[22%]">Type</Th>
              <Th className="w-[10%] text-center">Read-only</Th>
              <Th className="w-[14%]">Default</Th>
              {showCountryColumn ? (
                <Th className="w-[26%]">Available in</Th>
              ) : (
                <Th className="w-[18%]">Help text</Th>
              )}
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <FieldRow
                key={f.sys_id}
                field={f}
                striped={i % 2 === 1}
                showCountryColumn={showCountryColumn}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldRow({
  field,
  striped,
  showCountryColumn,
}: {
  field: FormField;
  striped?: boolean;
  showCountryColumn: boolean;
}) {
  const choices = field.choices ?? [];
  const isChoice = field.field_kind === 'choice_list' && choices.length > 0;
  const defaultText = humanizeDefault(field.default_value);
  const stripeCls = striped ? '[&>td]:bg-slate-100/80' : '';
  const loc = field.localization;

  return (
    <>
      <tr className={`border-t border-slate-100 align-top ${stripeCls}`}>
        <Td>
          <span className="font-medium text-slate-900">
            {field.question_text || field.name}
            {field.mandatory && (
              <span
                className="ml-0.5 text-rose-600 font-bold"
                aria-label="Mandatory"
                title="Mandatory"
              >
                *
              </span>
            )}
          </span>
          {loc?.has_unknown && (
            <span
              className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded px-1.5 py-0.5"
              title="A catalog client script touches this field but the parser couldn't classify it. Review the script in ServiceNow."
            >
              Dynamic
            </span>
          )}
        </Td>
        <Td>
          <TypeCell field={field} />
        </Td>
        <Td className="text-center">
          {field.read_only ? <ReadOnlyPill /> : <Dash />}
        </Td>
        <Td>{defaultText ?? <Dash />}</Td>
        {showCountryColumn ? (
          <Td>
            <CountryChips countries={loc?.visible_countries ?? []} />
          </Td>
        ) : (
          <Td className="text-slate-600">{field.help_text || <Dash />}</Td>
        )}
      </tr>
      {isChoice && (
        <tr className={`border-t border-slate-50 ${stripeCls}`}>
          <td colSpan={5} className="py-2 pr-4 pl-3">
            <ChoiceCluster choices={choices} />
          </td>
        </tr>
      )}
    </>
  );
}

function ChoiceCluster({ choices }: { choices: FormFieldChoice[] }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">
        Options
      </span>
      <div className="flex flex-wrap gap-1.5">
        {choices.map((c, i) => (
          <ChoiceChip key={i} choice={c} />
        ))}
      </div>
    </div>
  );
}

function ChoiceChip({ choice }: { choice: FormFieldChoice }) {
  const label = choice.text || choice.value || '(blank)';
  const isLocalization = isLocalizationChoice(choice);
  const valueHint =
    choice.value && choice.text && choice.value !== choice.text
      ? choice.value
      : null;
  const countryHint =
    isLocalization && (choice.available_in_countries?.length ?? 0) > 0
      ? `Available in: ${choice.available_in_countries!.join(', ')}`
      : null;
  const runtimeHint = choice.rebuilt_at_runtime
    ? "Choice list is rebuilt by ServiceNow at runtime — actual availability depends on the user's geography."
    : null;
  const title =
    [valueHint, countryHint, runtimeHint].filter(Boolean).join(' · ') ||
    undefined;

  return (
    <span
      className={
        'inline-flex items-center text-xs rounded-md px-2 py-0.5 border ' +
        (isLocalization
          ? 'text-amber-900 bg-amber-50 border-amber-300'
          : 'text-slate-700 bg-slate-100 border-slate-200')
      }
      title={title}
    >
      {label}
    </span>
  );
}

function CountryChips({ countries }: { countries: string[] }) {
  if (countries.length === 0) {
    return (
      <span
        className="text-xs text-slate-500 italic"
        title="The script didn't enumerate countries inline (likely a server-side mapping table). Check the source script in ServiceNow."
      >
        country-gated · countries unknown
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {countries.map((c) => (
        <span
          key={c}
          className="inline-flex items-center text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function ChoiceLegend({
  showAmber,
  showRuntime,
}: {
  showAmber: boolean;
  showRuntime: boolean;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded border border-slate-200 bg-slate-100" />
        Standard value
      </span>
      {showAmber && (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded border border-amber-300 bg-amber-50" />
          Localization · added by a country-branch script
        </span>
      )}
      {showRuntime && (
        <span className="text-slate-500">
          ⓘ Some dropdowns are rebuilt at runtime — actual values depend on
          the requester's geography.
        </span>
      )}
    </div>
  );
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={'pr-4 pb-2 font-semibold ' + className} scope="col">
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={'py-2.5 pr-4 align-top ' + className}>{children}</td>;
}

function TypeCell({ field }: { field: FormField }) {
  const baseType = field.type_name;
  if (!baseType) return <Dash />;

  const isReference =
    field.field_kind === 'reference_db' || field.field_kind === 'reference_ref';
  const refLabel = isReference ? field.reference_label : null;

  if (isReference && refLabel) {
    return (
      <div className="flex flex-col items-start gap-1 text-[11px]">
        <span className="text-brand-danone bg-brand-50 rounded-md px-2 py-0.5 whitespace-nowrap">
          {baseType}
        </span>
        <span className="text-slate-600 italic">{refLabel}</span>
      </div>
    );
  }

  return (
    <span className="inline-flex items-center text-[11px] text-brand-danone bg-brand-50 rounded-md px-2 py-0.5 whitespace-nowrap">
      {baseType}
    </span>
  );
}

/**
 * Form-level warning shown above the table when the KB declares
 * conditional logic on any field. Per-field rules are surfaced via the
 * Country-specific section + amber choice markers; this notice covers the
 * cases the parser couldn't classify and points reviewers at the source.
 */
function ConditionalLogicNotice({
  testTicketUrl,
  onOpenKb,
}: {
  testTicketUrl?: string | null;
  onOpenKb?: () => void;
}) {
  return (
    <aside
      role="note"
      className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-sm text-amber-900"
    >
      <span aria-hidden className="mt-0.5">⚠</span>
      <div className="leading-snug">
        <span className="font-semibold">Conditional logic in use.</span>{' '}
        Some fields may be hidden, made mandatory, or pre-filled depending on
        other answers. Per-field rules aren't surfaced here — check the{' '}
        {onOpenKb ? (
          <button
            type="button"
            onClick={onOpenKb}
            className="underline hover:no-underline"
          >
            KB article
          </button>
        ) : (
          'KB article'
        )}{' '}
        or{' '}
        {testTicketUrl ? (
          <a
            href={testTicketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            try the form
          </a>
        ) : (
          'try the form'
        )}{' '}
        in ServiceNow to see the live behavior.
      </div>
    </aside>
  );
}

function ReadOnlyPill() {
  return (
    <span
      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-50 text-brand-danone ring-1 ring-brand-100"
      title="Read-only"
      aria-label="Read-only"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M2 10s2.8-5 8-5 8 5 8 5-2.8 5-8 5-8-5-8-5z" />
        <circle cx="10" cy="10" r="2.4" />
      </svg>
    </span>
  );
}

function Dash() {
  return <span className="text-slate-300">—</span>;
}

function placementOf(f: FormField): FormFieldLocalization['placement'] {
  return f.localization?.placement ?? 'universal';
}

function isLocalizationChoice(c: FormFieldChoice): boolean {
  return c.localization_status === 'localization';
}

/**
 * ServiceNow ships a handful of well-known scripted defaults
 * (`javascript:gs.getUserID()` etc.) that are gibberish to a non-tech
 * reviewer. Map the common ones to plain English; for anything else
 * starting with `javascript:` flag it as "Computed value" so the reader
 * knows it's a runtime expression rather than a literal.
 */
function humanizeDefault(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;

  const expr = v.replace(/\s+/g, '');
  const map: Record<string, string> = {
    'javascript:gs.getUserID()': 'Currently logged-in user',
    'javascript:gs.getUser().getID()': 'Currently logged-in user',
    'javascript:gs.getUserName()': 'Currently logged-in user',
    'javascript:gs.getUser().getEmail()': "Logged-in user's email",
    'javascript:gs.getUser().getDisplayName()': "Logged-in user's name",
    'javascript:gs.getCurrentDate()': "Today's date",
    'javascript:gs.beginningOfToday()': "Start of today",
    'javascript:gs.endOfToday()': "End of today",
    'javascript:gs.nowDateTime()': "Current date & time",
    'javascript:newDate()': "Today's date",
  };
  if (map[expr]) return map[expr];
  if (expr.toLowerCase().startsWith('javascript:')) return 'Computed value';
  return v;
}

/** Pin Requested By / Requested For first; otherwise preserve SN's order. */
function sortRequesterFirst(fields: FormField[]): FormField[] {
  const priority: Record<string, number> = {
    requested_by: 0,
    requested_for: 1,
  };
  const decorated = fields.map((f, i) => ({
    f,
    rank: priority[(f.name || '').toLowerCase()] ?? 100 + i,
  }));
  decorated.sort((a, b) => a.rank - b.rank);
  return decorated.map((d) => d.f);
}
